// deps
import React, { memo, ReactElement, useRef, useMemo, useState, useCallback } from "react";

// styles
import { StyledText } from "../payment-success-banner/styles";
import {
    ButtonContainer,
    CloseCircleIconWrapper,
    CrossButton,
    ErrorContainer,
    ImageBackground,
    InputContainer,
    InputErrorContainer,
    InputWrapper,
    LableContainer,
    StyledInput,
    TextInputContainer,
    TipContainer,
    TipTitle,
} from "./styles";

// internal deps
import { BottomSheet, CloseIcon, BottomSheetChildrenProps, format } from "@swiggy-private/core-ui";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { Button } from "@swiggy-private/dls-web-core/dist/components/Button";
import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";
import { IButtonVariant } from "@swiggy-private/dls-web-core";
import { useKeyboardState } from "@swiggy-private/checkout-core";
import { TIP_TEST_IDS } from "@swiggy-private/payment-test-ids";

//constants
import { BILL_TEXTS, CONFIRM, TIP_AMOUNT_CONFIGS, tipBgImage } from "@dineout-payment-success-ui/constants";

//hooks
import { useTipAnalytics } from "@dineout-payment-success-ui/hooks/useDineoutPaymentSuccess";

//helpers
import { convertToCommaSeparated, sanitizeNumericInput } from "@dineout-payment-success-ui/helpers/BillHelper";
import { isInvalidTip } from "@dineout-payment-success-ui/utils/index";
import { getCloudinaryUrl } from "@swiggy-private/js-utils";

interface ITipModalProps {
    onConfirm?(data: number): void;
    close(): void;
    maxTipAmount?: number;
}

const TipModal = (props: ITipModalProps): ReactElement => {
    const { onConfirm, close, maxTipAmount = TIP_AMOUNT_CONFIGS.MAX_DEFAULT_TIP } = props;
    const [tipValue, setTipValue] = useState<number>(0);
    const defaultTipValue = tipValue > 0 ? tipValue.toString() : "";
    const [amount, setAmount] = useState(defaultTipValue);
    const [error, setError] = useState(false);

    const inputDivRef = useRef<HTMLInputElement>(null);
    useKeyboardState(inputDivRef);

    const minAmount = TIP_AMOUNT_CONFIGS.MIN_DEFAULT_TIP;

    const { onTipClosed } = useTipAnalytics();

    const onSubmit = useCallback(
        (input: number) => {
            onConfirm?.(input);
        },
        [onConfirm],
    );

    const closeHandler = useCallback(() => {
        onTipClosed();
        close();
    }, [onTipClosed, close]);

    const isTipValid = useCallback(
        (amount: number): boolean => {
            return amount >= minAmount && amount <= maxTipAmount;
        },
        [minAmount, maxTipAmount],
    );
    const submitDisabled = useMemo(() => !Number(amount), [amount]);

    const handleChange = useCallback(
        (val: string) => {
            const parsedVal = val?.replace(/,/g, "");
            if (isInvalidTip(parsedVal)) return;

            setError(!isTipValid(Number(parsedVal)));

            const finalText = sanitizeNumericInput(parsedVal);
            setAmount(finalText);
            setTipValue(Number(finalText));
        },
        [isTipValid, setError, setAmount, setTipValue],
    );

    const onSubmitEditing = useCallback(() => {
        if (isTipValid(Number(amount))) onConfirm?.(Number(amount));
    }, [isTipValid, amount, onConfirm]);

    const variant: IButtonVariant = useMemo(() => (error || submitDisabled ? "secondary" : "primary"), [
        error,
        submitDisabled,
    ]);
    const clearAmount = useCallback(() => {
        setAmount("");
    }, [setAmount]);

    const handleButtonClick = useCallback(() => {
        if (!submitDisabled && !error) {
            onSubmit(Number(amount) || 0);
        }
    }, [submitDisabled, error, onSubmit, amount]);

    return (
        <BottomSheet data-testid={TIP_TEST_IDS.TIP_CONTAINER} onClose={closeHandler} hideOnOverlayClick={true}>
            {(_bProps: BottomSheetChildrenProps): ReactElement => (
                <TipContainer ref={inputDivRef}>
                    <ImageBackground backgroundImage={getCloudinaryUrl(tipBgImage) || ""}>
                        <TipTitle>
                            <StyledText
                                variant="V2.subheading1.neutral.medium"
                                color="text_High_Emphasis"
                                data-testid={TIP_TEST_IDS.TIP_TITLE}
                            >
                                {BILL_TEXTS.ENTER_CUSTOM_TIP}
                            </StyledText>
                            <CrossButton onClick={closeHandler} data-testid={TIP_TEST_IDS.CUSTOM_TIP_CLOSE}>
                                <GenericIcon
                                    fillColor="background_Primary"
                                    strokeColor="background_Primary"
                                    Icon={CloseIcon}
                                />
                            </CrossButton>
                        </TipTitle>
                    </ImageBackground>
                    <InputContainer data-testid={TIP_TEST_IDS.CUSTOM_TIP_CONTAINER} ref={inputDivRef}>
                        <InputErrorContainer>
                            <LableContainer>
                                <StyledText variant="V2.body3.neutral.regular" color="primary">
                                    {BILL_TEXTS.ENTER_TIP_TEXT}
                                </StyledText>
                            </LableContainer>
                            <TextInputContainer>
                                <InputWrapper>
                                    {amount && <Text variant="V2.body1.neutral.bold">₹</Text>}
                                    <StyledInput
                                        data-testid={TIP_TEST_IDS.TIP_INPUT_BOX}
                                        type="text"
                                        inputMode="numeric"
                                        autoFocus
                                        onChange={event => {
                                            const val = event.target.value;
                                            handleChange(val);
                                        }}
                                        value={convertToCommaSeparated(amount)}
                                        onKeyDown={event => {
                                            if (event.key === "Enter") {
                                                onSubmitEditing();
                                            }
                                        }}
                                    />
                                </InputWrapper>

                                {amount && (
                                    <CloseCircleIconWrapper onClick={clearAmount}>
                                        <GenericIcon
                                            fillColor="background_Primary"
                                            strokeColor="background_Primary"
                                            Icon={CloseIcon}
                                        />
                                    </CloseCircleIconWrapper>
                                )}
                            </TextInputContainer>
                        </InputErrorContainer>
                        <ErrorContainer>
                            {error && (
                                <StyledText
                                    variant="V2.body3.neutral.regular"
                                    color="negative"
                                    data-testid={TIP_TEST_IDS.ERROR}
                                >
                                    {format(BILL_TEXTS.VALUE_RANGE, String(minAmount), String(maxTipAmount))}
                                </StyledText>
                            )}
                        </ErrorContainer>
                        <ButtonContainer>
                            <Button key={variant} onClick={handleButtonClick} variant={variant} buttonSize="medium">
                                <StyledText
                                    variant="V2.subheading1.neutral.bold"
                                    color="background_Primary"
                                    data-testid={TIP_TEST_IDS.CONFIRM_TIP_BUTTON}
                                >
                                    {CONFIRM}
                                </StyledText>
                            </Button>
                        </ButtonContainer>
                    </InputContainer>
                </TipContainer>
            )}
        </BottomSheet>
    );
};

export default memo(TipModal);
